import { inject, Injectable } from "@angular/core";
import { I_TextColorsResponse } from "../models/display/items/colors/text-colors";
import { C_CellConfig } from "../models/display/items/home/domain/card-config.model";
import { DisplaysService } from "../services/displays.service";
import {
  asyncScheduler, combineLatest, EMPTY, filter, finalize, map, merge, Observable, of, shareReplay, startWith, switchMap, take,/*,finalize */
  throttleTime
} from "rxjs";
import { C_ObservationWrapper } from "../models/display/items/observation/domain/observation-wrapper.model";
import { C_Observation } from "../models/display/items/observation/domain/observation.model";
import { ObservationTextRule } from "../models/display/items/observation/domain/observation-text-rule.model";
import { E_ObservationValueType } from "../enums/observation/observation-value-type.enum";
import { C_ObservationColorRule } from "../models/display/items/observation/domain/observation-color-rule.model";
import { C_ConfigObservation } from "../models/display/items/observation/domain/config-observation.model";
import { C_PointOfCareInfo } from "../models/display/items/point-of-care/domain/point-of-care-config.model";
import { addSeconds } from "date-fns";
import { GlobalTimerService } from "../services/global-timer.service";
import { I_ObsState } from "../models/display/items/observation/domain/observation-state.model";
import { C_MasterList } from "../models/unit/items/master-lists/domain/master-list.model";
import { C_DisplayData } from "../models/display/domain/display.model";
import { I_MasterListNames, MASTER_MAP, T_UnitMasterListKeys } from "../models/unit/domain/unit.model";
import { C_GroupedObservation } from "../models/display/items/grouped-obs/domain/grouped-observation.model";
import { I_GroupedObsState } from "../models/display/items/grouped-obs/domain/grouped-observation-state.model";
import { E_GraphSerieSourceType } from "../enums/smart-display/graphs/source-type.enum";
import { C_PatientData, E_PatientDataChangeAction } from "../models/display/items/patient/domain/patient.model";
import { I_CurrentDisplay } from "../models/display/api/display.response";
import { C_ObservationAlarmData } from "../models/display/items/observation/domain/observation-alarm-data.model";


@Injectable({
  providedIn: 'root'
})
export class ObsUtilsService {

  private displayService = inject(DisplaysService);
  private globalTimer = inject(GlobalTimerService);

  private expirationCache = new Map<string, Observable<boolean>>();
  private displayCache = new Map<string, Observable<C_DisplayData>>();

  private currentDisplay$: Observable<C_DisplayData | null> = this.displayService.getCurrentDisplay().pipe(
    switchMap((cd: I_CurrentDisplay | null): Observable<C_DisplayData | null> => {
      if (!cd) return of(null);
      const cacheKey = `${cd.id}_${cd.type}`;
      if (this.displayCache.has(cacheKey)) {
        return this.displayCache.get(cacheKey)!;
      }
      const displayStream$ = this.displayService.getDisplay(cd.id, cd.type).pipe(
        filter((dis): dis is C_DisplayData => !!dis),
        // shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => {
          this.displayCache.delete(cacheKey);
        })
      );
      this.displayCache.set(cacheKey, displayStream$);
      return displayStream$;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private sharedExpirationStream$ = this.globalTimer.getAllTimersObservable().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor() { }

  getCurrentDisplay() {
    return this.currentDisplay$;
  }

  // public getLiveGlobalAlarmsWithPatient(): Observable<C_ObservationAlarmData[]> {
  //   return this.getCurrentDisplay().pipe(
  //     switchMap(display => {
  //       if (!display) return of([]);

  //       return display.alarmList.getLiveGlobalAlarms().pipe(
  //         switchMap(alarms => {
  //           if (!alarms || alarms.length === 0) {
  //             return of([]);
  //           }

  //           // Mapeamos cada alarma a un stream que combine la alarma con su paciente
  //           const alarmsWithPatients$ = alarms.map(alarm => {
  //             if (!alarm.patientId) {
  //               return of(new C_ObservationAlarmWithLivePatient(alarm, undefined));
  //             }

  //             return this.getPatientStream(alarm.patientId).pipe(
  //               map(patient => new C_ObservationAlarmWithLivePatient(alarm, patient))
  //             );
  //           });

  //           // Unificamos los streams para emitir cuando cambie cualquier alarma o cualquier paciente
  //           return combineLatest(alarmsWithPatients$);
  //         })
  //       );
  //     })
  //   );
  // }

  /**
     * Recupera un flujo reactivo de una alarma específica desde la lista del display.
     */
  private getAlarmStream(alarmId: string, display: C_DisplayData): Observable<C_ObservationAlarmData | undefined> {
    // Usamos el método reactivo que definimos en la arquitectura de C_AlarmList
    return display.alarmList.getLiveAlarm(alarmId);
  }
  getLivePocById(id: string): Observable<C_PointOfCareInfo> {
    return this.currentDisplay$.pipe(
      // 1. Nos aseguramos de tener datos de pantalla válidos
      filter((display): display is C_DisplayData => !!display),
      switchMap(display => {
        // 2. Extraemos el PointOfCare del mapa de forma segura
        const poc = display.displayInfo?.pointOfCares?.get(id);

        // Si el ID no existe en el mapa, devolvemos un observable vacío o EMPTY de RxJS para no romper el flujo
        if (!poc) {
          return EMPTY; // O importa 'EMPTY' de 'rxjs' y haz: return EMPTY;
        }

        // 3. Retornamos directamente el flujo de livePoc sin anidar switchMaps innecesarios
        return poc.getLivePoc();
      })
    );
  }
  /**
   * Si el POC tiene una lista de IDs de alarmas, este método devuelve un stream con los objetos completos.
   */
  getLiveAlarmsForPoc(poc: C_PointOfCareInfo): Observable<C_ObservationAlarmData[]> {
    return this.currentDisplay$.pipe(
      filter((display): display is C_DisplayData => !!display),
      switchMap(display =>
        poc.getLivePoc().pipe(
          switchMap(livePoc => {
            const alarmIds = Array.from(livePoc.pocConfig.alarmIds);
            if (alarmIds.length === 0) return of([]);

            // Para cada ID, obtenemos la alarma Y su estado de expiración
            const alarmStreams = alarmIds.map(id =>
              this.getAlarmStream(id, display).pipe(
                switchMap(alarm => {
                  if (!alarm) return of(null);

                  // Combinamos la alarma con su propio timer de expiración
                  return this.getAlarmExpirationStream(alarm).pipe(
                    map(isExpired => isExpired ? null : alarm)
                  );
                })
              )
            );

            return combineLatest(alarmStreams).pipe(
              map(alarms => alarms.filter((a): a is C_ObservationAlarmData => !!a)),
            );
          })
        )
      )
    );
  }


  /**
   * Genera un flujo que emite 'true' cuando una alarma específica caduca.
   */
  private getAlarmExpirationStream(alarm: C_ObservationAlarmData | undefined): Observable<boolean> {
    // 1. Validaciones previas
    if (!alarm || !alarm.time || !alarm.expires) return of(false);
    if (alarm.isExpired()) return of(true);

    // 2. Clave de caché única (ID Alarma + Expiración + Timestamp del dato)
    // Esto permite que múltiples componentes compartan el mismo timer para la misma alarma
    const alarmTime = new Date(alarm.time).getTime();
    const cacheKey = `alarm_${alarm.id}_${alarm.expires}_${alarmTime}`;

    if (this.expirationCache.has(cacheKey)) {
      return this.expirationCache.get(cacheKey)!;
    }

    // 3. Cálculo de tiempo restante
    const expirationTime = alarmTime + (alarm.expires * 1000);
    const diffMs = expirationTime - Date.now();

    if (diffMs <= 0) return of(true);

    // 4. Registro en el GlobalTimer optimizado
    const timerId = this.globalTimer.createTimerWithDuration(diffMs);

    const expiration$ = this.sharedExpirationStream$.pipe(
      filter(expiredIds => expiredIds.has(timerId)),
      map(() => true),
      startWith(false),
      take(2),
      finalize(() => {
        this.expirationCache.delete(cacheKey);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.expirationCache.set(cacheKey, expiration$);
    return expiration$;
  }

  /**
   * Crea o recupera un flujo para la definición/configuración de una observación.
   * Escucha cambios tanto en la lista (C_ConfigObservationList) como internos del objeto.
   */
  private getConfigObsStream(obsName: string): Observable<C_ConfigObservation | undefined> {
    const configObsStream$ = this.currentDisplay$.pipe(switchMap(display => {
      const stream$ = display?.configObservationList.getLiveConfigObs(obsName);
      if (!stream$) {
        return EMPTY;// Manejo de error o retorno vacío si no existe
      }
      return stream$;
    }));
    return configObsStream$
  }

  /**
   * Busca en la lista global indexada por el paciente del POC.
   */
  private getObservationStream(obsName: string, poc: C_PointOfCareInfo, display: C_DisplayData): Observable<C_ObservationWrapper | undefined> {
    const patientId = poc.pocConfig.patientId;
    if (!patientId) return of(undefined);

    // Accedemos a la nueva lista desacoplada que vive en el display
    return display.observationsList.getLiveObs(patientId, obsName);
  }

  private getGroupedObservationStream(graphName: string, poc: C_PointOfCareInfo, display: C_DisplayData): Observable<C_GroupedObservation | undefined> {
    const patientId = poc.pocConfig.patientId;
    if (!patientId) return of(undefined);

    // Accedemos a la nueva lista de agrupadas desacoplada
    return display.groupedObservationsList.getLiveGroupedObs(patientId, graphName);
  }


  // TODO: Evaluate its use when the subscription to multiple observations is enabled.
  // private evaluateLatestObservation() {
  //   const obsNames = this.cellInfo?.observationName;
  //   if (!obsNames) return;
  //   let latestObs: C_ObservationWrapper | undefined;

  //   obsNames.forEach(name => {
  //     const currentObs = this.poc?.pocConfig.observations?.get(name);
  //     const currentTime = currentObs?.currentValue?.time?.valueOf();
  //     const latestTime = latestObs?.currentValue?.time?.valueOf();

  //     if (currentTime) {
  //       if (!latestObs || !latestTime || currentTime > latestTime) {
  //         latestObs = currentObs;
  //       }
  //     }
  //   });
  //   if (latestObs) {
  //     this.observation = latestObs;
  //   }
  // }

  private getExpirationStream(observation: C_ObservationWrapper | undefined, obsDef: C_ConfigObservation | undefined): Observable<boolean> {
    if (!observation || !obsDef || !observation.currentValue?.time) return of(false);
    if (observation.currentValue.isExpired()) return of(true);

    const expireSeconds = obsDef.expires ?? 60;
    // La clave de caché ahora incluye el timestamp del dato.
    // Si 50 celdas ven la misma FC del mismo paciente, compartirán este stream.
    const cacheKey = `${observation.id}_${expireSeconds}_${observation.currentValue.time.getTime()}`;

    if (this.expirationCache.has(cacheKey)) {
      return this.expirationCache.get(cacheKey)!;
    }

    const expirationTime = addSeconds(new Date(observation.currentValue.time), expireSeconds);
    const diffMs = expirationTime.getTime() - Date.now();

    if (diffMs <= 0) return of(true);

    const timerId = this.globalTimer.createTimerWithDuration(diffMs);

    const expiration$ = this.sharedExpirationStream$.pipe(
      // Aquí está el truco: ya no usamos el filter pesado de includes en cada celda
      // Solo comprobamos si el timerId generado para ESTE valor está en el lote de terminados
      filter(expiredIds => expiredIds.has(timerId)),
      map(() => true),
      startWith(false),
      take(2),
      finalize(() => {
        this.expirationCache.delete(cacheKey);
        // Opcional: podrías añadir lógica para limpiar el timer si nadie escucha,
        // pero tu servicio ya se encarga de la auto-limpieza al expirar.
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.expirationCache.set(cacheKey, expiration$);
    return expiration$;
  }

  private getMasterStream(display: C_DisplayData, req: keyof I_MasterListNames): Observable<C_MasterList> {
    const unitProperty = MASTER_MAP[req] as T_UnitMasterListKeys;
    return display.unit.getLiveList(unitProperty);
  }


  /**
   * Función principal que consumen los componentes.
   * Combina configuración, datos y tiempo de expiración en un solo estado atómico.
   */
  getCellStream(cellInfo: C_CellConfig | undefined, poc: C_PointOfCareInfo, mastersReq: I_MasterListNames = {}): Observable<I_ObsState> {
    return this.getBaseTriggers(cellInfo, poc).pipe(
      switchMap(() => this.currentDisplay$),
      filter((display): display is C_DisplayData => !!display),
      switchMap((display) => {
        const cC = cellInfo;
        const obsName = cC?.observationName?.[0] ?? cC?.name;

        // 1. Resolvemos los Masters de forma independiente (común para A y B)
        const requestedKeys = Object.keys(mastersReq).filter(k => mastersReq[k as keyof I_MasterListNames]);
        const masters$ = requestedKeys.length > 0
          ? combineLatest(
            requestedKeys.map(key =>
              // Forzamos que si el método devuelve undefined, usemos un observable vacío
              this.getMasterStream(display, key as keyof I_MasterListNames) || of(new C_MasterList())
            )
          ).pipe(
            map((lists: C_MasterList[]) =>
              lists.reduce((acc, list, index) => {
                acc[requestedKeys[index]] = list;
                return acc;
              }, {} as Record<string, C_MasterList>)
            ),
            startWith({})
          )
          : of({});

        // 2. Combinamos los Masters con la lógica de la celda
        return masters$.pipe(
          switchMap((mastersData) => {
            // --- CASO A: CELDA ESTÁTICA O SIN OBSERVACIÓN ---
            if (cC?.isStatic || !obsName) {
              return of({
                observation: {} as C_ObservationWrapper,
                obsDef: {} as C_ConfigObservation,
                value: cC?.name || '',
                color: '',
                isExpired: false,
                display,
                masters: mastersData, // Ahora se incluyen aquí
              } as I_ObsState);
            }
            // --- CASO B: CELDA REACTIVA (CON OBSERVACIÓN) ---
            return combineLatest({
              obsDef: this.getConfigObsStream(obsName),
              observation: this.getObservationStream(obsName, poc, display),
              alarms: this.getLiveAlarmsForPoc(poc) ?? []
            }).pipe(
              switchMap(({ obsDef, observation, alarms }) => {
                // Combinamos Expiración
                return this.getExpirationStream(observation, obsDef).pipe(
                  throttleTime(25, asyncScheduler, { leading: false, trailing: true }),
                  map((isExpired) => ({
                    obsDef,
                    observation,
                    isExpired,
                    masters: mastersData, // Disponibles aquí también
                    display,
                    alarms,
                  }))
                );
              }),
              // Mapeo final de reglas visuales (Texto y Color)
              map((state) => {
                const specialCases = ["DoctorObs", "PassiveSittingObs", "AllergyObs", "LanguageBarrierObs", "InsulationObs", "MobilityOptionObs"];
                const obsName = state.obsDef?.name
                if (obsName && specialCases.includes(obsName)) return {
                  ...state,
                  value: state.observation?.currentValue?.value,
                  color: ""
                } as I_ObsState;
                let val: any = state.observation?.currentValue?.value;
                let color: any;
                if (Array.isArray(val)) {
                  const values: any[] = [];
                  const colors: string[] = [];
                  val.forEach(icon => {
                    const evaluated = this.evaluateValueText(icon, cC?.textRules ?? []);
                    if (!evaluated) return;
                    values.push(evaluated);
                    colors.push(this.evaluateValueColor(evaluated, state.obsDef?.colorRules || []));
                  });
                  val = values;
                  color = colors;
                } else {
                  val = this.evaluateValueText(val, cC?.textRules ?? []);
                  val = (val !== null && val !== undefined) ? String(val) : '';
                  color = this.evaluateValueColor(val, state.obsDef?.colorRules || [], state.observation?.currentValue?.status);
                }
                return {
                  ...state,
                  value: val,
                  color: color
                } as I_ObsState;
              })
            );
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }


  /**
   * Crea el flujo base de eventos que cualquier celda con dependencia de observaciones (simples o agrupadas) necesita.
   */
  private getBaseTriggers(cellInfo: C_CellConfig | undefined, poc: C_PointOfCareInfo): Observable<I_TriggerChange> {
    return merge(
      (cellInfo?.getForcedChanges() ?? EMPTY).pipe(
        map(c => ({
          origin: E_TriggerChanges.CellConfig,
          action: (c as any)?.action,
          data: (c as any)?.data
        }))
      ),
      poc.getLivePoc().pipe(
        map(() => ({ origin: E_TriggerChanges.Poc }))
      )
    ).pipe(
      startWith({ origin: E_TriggerChanges.Initial } as I_TriggerChange)
    );
  }

  /**
   * Flujo para celdas gráficas/agrupadas.
   * No evalúa reglas de texto/color individuales, devuelve el objeto de grupo reactivo.
   */
  getGroupedCellStream(cellInfo: C_CellConfig, poc: C_PointOfCareInfo): Observable<I_GroupedObsState> {
    const graphName = cellInfo?.graphConf;
    if (!graphName) return EMPTY;

    return this.getBaseTriggers(cellInfo, poc).pipe(
      switchMap(() => this.currentDisplay$),
      filter((display): display is C_DisplayData => !!display),
      switchMap(display => {
        const obsOnGraph = display.configObservationList.get(graphName);
        // 1. Obtenemos la configuración del gráfico primero para saber qué keys filtrar
        return display.displayInfo.chartConfig.getLiveChartGroup(graphName, obsOnGraph).pipe(
          switchMap(graphConfiguration => {

            // Extraemos las keys relevantes (Obs y Tratamientos) de la configuración
            const relevantTreatments = graphConfiguration?.seriesConfig
              .filter(s => s.sourceType === E_GraphSerieSourceType.Treatment)
              .map(t => t.key);

            const relevantObs = graphConfiguration?.seriesConfig
              .filter(s => s.sourceType === E_GraphSerieSourceType.Obs)
              .map(t => t.key);

            // 2. Flujo de datos del grupo (Gráfica per se)
            const groupedObs$ = this.getGroupedObservationStream(graphName, poc, display);

            // 3. Cambios colaterales FILTRADOS (Paciente)
            const patId = poc.pocConfig.patientId;
            const patientChanges$ = patId && relevantObs && relevantTreatments
              ? this.getFilteredPatientChanges(display, patId, relevantObs, relevantTreatments)
              : of(null);

            return combineLatest({
              groupedObs: groupedObs$,
              sideChanges: patientChanges$
            }).pipe(
              map(({ groupedObs }) => ({
                display,
                graphConfiguration,
                groupedObs,
                treatments: relevantTreatments,
                observations: relevantObs
              } as I_GroupedObsState))
            );
          })
        );
      }),
      throttleTime(25, asyncScheduler, { leading: false, trailing: true }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Escucha cambios del paciente pero solo emite si afectan a las series configuradas.
   */
  private getFilteredPatientChanges(
    display: C_DisplayData,
    patId: string,
    obsKeys: string[],
    treatmentKeys: string[]
  ): Observable<any> {
    const patientsList = display.patientList;

    // 1. Usamos el método reactivo optimizado que filtra ruidos de otros pacientes
    return patientsList.getLivePatient(patId).pipe(
      switchMap(patient => {
        if (!patient) return of(null);

        // 2. Escuchamos los cambios específicos de ESTE paciente con un único stream
        return patient.getForcedChanges().pipe(
          filter(change => {
            // Si el cambio es una actualización general (cama, datos personales, etc.),
            // no nos interesan los filtros de negocio de esta vista. Cortamos flujo.
            if (change.action === E_PatientDataChangeAction.GeneralUpdate) {
              return false;
            }

            // Filtro estricto para actualizaciones de Observaciones
            if (change.action === E_PatientDataChangeAction.ObservationsUpdate) {
              return change.ids.some(id => obsKeys.includes(id));
            }

            // Filtro estricto para actualizaciones de Tratamientos
            if (change.action === E_PatientDataChangeAction.TreatmentsUpdate) {
              return change.codes.some(code => treatmentKeys.includes(code));
            }

            // Filtro estricto para Observaciones Agrupadas (si aplica en este flujo)
            if (change.action === E_PatientDataChangeAction.GroupedObservationsUpdate) {
              return change.groups.some(group => obsKeys.includes(group)); // O las llaves de grupo correspondientes
            }

            return false;
          }),
          // Emitimos una señal o el propio objeto mutado para que el componente pinte
          map(() => patient),
          // Nos aseguramos de disparar la primera carga nada más suscribirse
          startWith(patient)
        );
      })
    );
  }

  /**
   * Function to determine the color of the observation based on its status: ok, alert, or warning.
   *
   * Includes special cases for different observations, mainly for the customization of the Ramón y Cajal hospital ('ryc').
   */
  checkColor(
    currentValue: C_Observation | undefined,
    obsDef?: C_ConfigObservation,
    indicatorcolor?: I_TextColorsResponse,
    cellName?: string
  ): { isExpired: boolean; color: string } {

    if (!obsDef || !indicatorcolor || !currentValue) {
      return { isExpired: false, color: indicatorcolor?.normal ?? '' };
    }

    const { status, isExpired } = currentValue;
    const isExpiredValue = isExpired();
    const defaultColor = indicatorcolor["normal"];
    const statusKey = status?.toLowerCase() as keyof I_TextColorsResponse || 'normal';
    const color = statusKey === 'ok' ? indicatorcolor['normal'] : indicatorcolor[statusKey] ?? defaultColor;

    const specialCases = ["gcs", "rass", "cam_icu", "isolation", "allergies"];
    if (obsDef.name && obsDef.name.toLowerCase() == "complexity") {
      if (currentValue.status?.toLowerCase() == "ok") return { isExpired: false, color: "#60d61d" };
      return { isExpired: false, color: color };
    }
    if (obsDef.name && specialCases.includes(obsDef.name.toLowerCase())) {
      return { isExpired: false, color: defaultColor };
    }
    if (isExpiredValue) {
      return { isExpired: true, color: defaultColor };
    }
    return { isExpired: false, color };
  }

  evaluateValueColor(
    value: number | string | boolean | Date | undefined, rules: C_ObservationColorRule[], status?: string | undefined): string {
    for (const rule of rules) {
      switch (rule.valueType) {
        case E_ObservationValueType.Number:
          try {
            value = Number(value);
            if (
              (rule.min != null && value < rule.min)
              ||
              (rule.max != null && value >= rule.max)
            ) {
              continue;
            }
            return rule.color;
          } catch (error) {
            continue;
          }
        case E_ObservationValueType.String:
          if (typeof value !== 'string') continue;
          if (rule.matchText != null && value === rule.matchText) return rule.color;
          break;
        case E_ObservationValueType.Boolean:
          try {
            value = !!value;
            if (rule.matchBoolean === value) return rule.color;
          } catch (error) {
            continue;
          }
          break;
        case E_ObservationValueType.Date:
          try {
            value = new Date(value as string | number);
            const dateValue = value.getTime();
            if (isNaN(dateValue)) continue;
            const min = rule.minDate ? new Date(rule.minDate).getTime() : undefined;
            const max = rule.maxDate ? new Date(rule.maxDate).getTime() : undefined;
            if (min != null && dateValue < min) continue;
            if (max != null && dateValue > max) continue;
            return rule.color;
          } catch (error) {
            continue;
          }
      }
    }
    return status != undefined ?
      status.toLowerCase() == 'warning' ? '#fffb4d' : status.toLowerCase() == 'alert' ? '#F9627B' : ''
      : '';
  }

  evaluateValueText(
    obsVal: number | string | boolean | Date | undefined,
    rules: ObservationTextRule[]
  ): number | string | boolean | Date | undefined {
    for (const rule of rules) {
      switch (rule.valueType) {
        case E_ObservationValueType.Number:
          try {
            const numberVal = Number(obsVal);
            // Check for matchNumber first
            if (rule.matchNumber != null && numberVal === rule.matchNumber) {
              return rule.value;
            }
            if (
              (rule.minNum != null && numberVal < rule.minNum)
              ||
              (rule.maxNum != null && numberVal >= rule.maxNum)
            ) {
              continue;
            }
            return rule.value;
          } catch (error) {
            continue;
          }
        case E_ObservationValueType.String:
          if (typeof obsVal !== 'string') continue;
          if (rule.matchText != null && obsVal === rule.matchText) return rule.value;
          break;
        case E_ObservationValueType.Boolean:
          try {
            const boolVal = !!obsVal;
            if (rule.matchBoolean === boolVal) return rule.value;
          } catch (error) {
            continue;
          }
          break;
        case E_ObservationValueType.Date:
          try {
            const stringVal = new Date(obsVal as string | number);
            const dateValue = stringVal.getTime();
            if (isNaN(dateValue)) continue;
            const min = rule.minDate ? new Date(rule.minDate).getTime() : undefined;
            const max = rule.maxDate ? new Date(rule.maxDate).getTime() : undefined;
            if (min != null && dateValue < min) continue;
            if (max != null && dateValue > max) continue;
            return rule.value;
          } catch (error) {
            continue;
          }
      }
    }
    return obsVal;
  }

  prepareObs(cellInfo: C_CellConfig | undefined, observation?: C_ObservationWrapper): Observable<{ obsDef: C_ConfigObservation; currentColor: string; isExpired: boolean }> {
    // 1. Usamos el flujo cacheado y optimizado
    return this.currentDisplay$.pipe(
      filter((display): display is C_DisplayData => !!display),
      switchMap(d => {
        const propertyName = cellInfo?.name;
        if (!propertyName) return EMPTY;

        // 2. Escuchamos cambios en la lista de definiciones de forma reactiva
        // Usamos el mismo patrón que con las observaciones para que no se quede "colgado"
        return d.configObservationList.getForcedChanges().pipe(
          startWith(null),
          map(() => d.configObservationList.get(propertyName)),
          // Solo continuamos si encontramos la definición
          filter(obsDef => !!obsDef),
          map(obsDef => {
            // 3. Calculamos color y expiración (usando tu lógica de checkColor)
            const obsData = this.checkColor(observation?.currentValue);
            return {
              obsDef: obsDef!,
              currentColor: obsData.color,
              isExpired: obsData.isExpired
            };
          })
        );
      })
    );
  }

  /**
   *
   * @param value
   */
  getFormattedValue(value: number | string, decimalFormat: number, useCommaForDecimals: boolean = true): string {
    if (decimalFormat === null) return value?.toString() ?? '';
    if (value == null || value == undefined) return '';

    // Logic to format the number
    const formatNumber = (num: number): string => {
      // Check if the number is an integer
      const isInteger = Number.isInteger(num);

      // If it's an integer, we do not apply decimals
      if (isInteger) {
        return num.toString();
      }

      // Convert the number to a fixed decimal format
      let formatted = num.toFixed(decimalFormat);

      // Replace the decimal separator if needed
      return useCommaForDecimals
        ? formatted.replace('.', ',')
        : formatted;
    };

    // Checks the type of value and formats it accordingly
    if (typeof value === 'number') {
      return formatNumber(value);
    } else if (typeof value === 'string' && !isNaN(parseFloat(value)) && !this.isValidDateTimeFormat(value)) {
      return formatNumber(parseFloat(value));
    } else {
      return value.toString();
    }
  }

  /**
   * Checks if a given string matches the "DD/MM/YYYY HH:MM" date and time format.
   *
   * @param inputString The string to validate.
   * @returns True if the string matches the format, false otherwise.
   */
  private isValidDateTimeFormat(inputString: string): boolean {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/;
    return regex.test(inputString);
  }

  getPatientStream(patId: string): Observable<C_PatientData | undefined> {
    if (!patId) return of(undefined);
    const patientStream$ = this.currentDisplay$.pipe(switchMap(display => {
      const stream$ = display?.patientList.getLivePatient(patId);
      if (!stream$) {
        return EMPTY;// Manejo de error o retorno vacío si no existe
      }
      return stream$;
    }));
    return patientStream$
  }

}

interface I_TriggerChange {
  origin: E_TriggerChanges;
  action?: any; // enum de acciones
  data?: any; // cambios crudos
}

enum E_TriggerChanges {
  CellConfig = "cellConfig",
  Poc = "poc",
  PocConfig = "pocConfig",
  Initial = "initial",
}
